import { addMonths, addYears, format } from "date-fns";
import { setFormValues } from "../CreateCertificateModalSlice";
import { Dispatch } from "redux";
import { ValidityPeriodInterface } from "../../../common/interfaces/ValidityPeriodInterface";
import { QualificationValidityTypeEnum } from "../../../common/enums/QualificationValidityTypeEnum";

const DATE_FORMAT = "dd-MM-yyyy";

interface Props {
    formValues: {
        validAt: string;
        qualification: {
            validityPeriod: ValidityPeriodInterface | null;
            validityType?: QualificationValidityTypeEnum | null;
            fixedValidUntil?: string | null;
        };
        expiresAt: string;
    };
    dispatch: Dispatch;
}

type Qualification = Props["formValues"]["qualification"];

const getFixedDateExpiry = (qualification?: Qualification): string | null => {
    if (
        qualification?.validityType !==
            QualificationValidityTypeEnum.FIXED_DATE ||
        !qualification.fixedValidUntil
    ) {
        return null;
    }

    return format(qualification.fixedValidUntil, DATE_FORMAT);
};

const getPeriodExpiry = (
    validAt?: string,
    validityPeriod?: ValidityPeriodInterface | null,
): string | null => {
    if (!validAt || (!validityPeriod?.years && !validityPeriod?.months)) {
        return null;
    }

    let expiresAtDate = new Date(validAt);

    if (validityPeriod.years) {
        expiresAtDate = addYears(expiresAtDate, validityPeriod.years);
    }

    if (validityPeriod.months) {
        expiresAtDate = addMonths(expiresAtDate, validityPeriod.months);
    }

    return format(expiresAtDate, DATE_FORMAT);
};

const updateExpiresAt = (
    formValues: Props["formValues"],
    dispatch: Dispatch,
    expiresAt: string | null,
): void => {
    if (formValues.expiresAt === expiresAt) {
        return;
    }

    dispatch(setFormValues({ ...formValues, expiresAt }));
};

export const calculateExpiresAt = (props: Props): void => {
    const { formValues, dispatch } = props;
    const qualification = formValues?.qualification;

    const fixedExpiry = getFixedDateExpiry(qualification);
    if (fixedExpiry) {
        updateExpiresAt(formValues, dispatch, fixedExpiry);
        return;
    }

    const periodExpiry = getPeriodExpiry(
        formValues?.validAt,
        qualification?.validityPeriod,
    );
    if (periodExpiry) {
        updateExpiresAt(formValues, dispatch, periodExpiry);
        return;
    }

    if (!formValues?.validAt) {
        return;
    }

    updateExpiresAt(formValues, dispatch, null);
};
